/** @format */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Alert,
  CircularProgress,
  styled,
} from '@mui/material';
import { Https, Visibility, VisibilityOff } from '@mui/icons-material';

import Symbol from '../../../Assets/SVG/symbol';
import img from '../../../Assets/login.jpg';
import { resetForgotPassword } from '../../../Services/keycloak/keycloak.service';
import { isTokenValid, getAuthToken, clearAuthSession } from '../../../Util/authUtils';

const AuthContainer = styled(Box, { label: 'authPaper' })({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 24,
  padding: 24,
  width: '340px',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -70%)',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
});

const inputSx = {
  '& .MuiInputLabel-root': { color: 'white', fontSize: 12 },
  '& .MuiOutlinedInput-root': {
    color: 'white',
    fontSize: 14,
    '& fieldset': { borderColor: 'white' },
  },
  '& .MuiFormHelperText-root': {
    color: '#ff8a80',
    fontSize: 12,
    marginLeft: 0,
  },
};

const titleSx = { color: 'white', fontSize: 14, fontWeight: 600 };
const alertSx = {
  width: '100%',
  fontSize: 12,
  '& .MuiAlert-message': { fontSize: 12 },
};

function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    severity: 'info',
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  useEffect(() => {
    if (isTokenValid(getAuthToken())) clearAuthSession();
  }, []);

  const validate = () => {
    const temp = {};
    if (!newPassword || newPassword.length < 8)
      temp.newPassword = 'Password must be at least 8 characters';
    if (newPassword !== confirmPassword)
      temp.confirmPassword = 'Passwords do not match';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!resetToken) {
      setAlert({
        show: true,
        message: 'Invalid or expired reset link',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      await resetForgotPassword({
        newPassword,
        confirmPassword,
        resetToken,
      });
      clearAuthSession();
      setAlert({
        show: true,
        message: 'Password reset successful. Redirecting to login...',
        severity: 'success',
      });
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (error) {
      setAlert({
        show: true,
        message: error?.response?.data?.message || 'Reset link expired or invalid',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment
      onClick={() => setShowPassword((prev) => !prev)}
      sx={{ cursor: 'pointer', color: 'white' }}
      position="end"
    >
      {showPassword ? <VisibilityOff /> : <Visibility />}
    </InputAdornment>
  );

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        height: '100%',
        background: `linear-gradient(#00000090,#00000090),url(${img})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <AuthContainer>
        <Box>
          <Symbol width="40" height="40" color="#fafafa" />
        </Box>
        <Typography variant="body2" sx={titleSx}>
          Reset Password
        </Typography>

        {alert.show && (
          <Alert severity={alert.severity} sx={alertSx}>
            {alert.message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleResetPassword}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%',
          }}
        >
          <TextField
            size="small"
            required
            type={showPassword ? 'text' : 'password'}
            label="new password"
            variant="outlined"
            autoComplete="new-password"
            value={newPassword}
            sx={inputSx}
            helperText={errors.newPassword || ' '}
            error={Boolean(errors.newPassword)}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors((prev) => ({ ...prev, newPassword: '' }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'white' }}>
                  <Https />
                </InputAdornment>
              ),
              endAdornment: passwordAdornment,
            }}
          />
          <TextField
            size="small"
            required
            type={showPassword ? 'text' : 'password'}
            label="confirm password"
            variant="outlined"
            autoComplete="new-password"
            value={confirmPassword}
            sx={inputSx}
            helperText={errors.confirmPassword || ' '}
            error={Boolean(errors.confirmPassword)}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors((prev) => ({ ...prev, confirmPassword: '' }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'white' }}>
                  <Https />
                </InputAdornment>
              ),
              endAdornment: passwordAdornment,
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            sx={{ fontSize: 14 }}
          >
            Reset Password
          </Button>
          <Button
            size="small"
            sx={{ color: 'white', fontSize: 13, mt: '-8px' }}
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </Box>
      </AuthContainer>
    </Box>
  );
}

export default ResetPassword;
